import * as d3 from 'd3';
import ChartComponent from './ChartComponent';
import React from 'react';
import "./piechart.scss";

const Pie = ({
    innerRadius,
    outerRadius,
    sorted,
    data,
    animation,
    isUpdateDisabled,
    colorFunction,
    hoverColors,
    showTooltip = true,
    expanding = true,
    statistic = {},
    onMouseOver = () => { },
    onMouseOut = () => { }
}) => {
    let diameter = (outerRadius + innerRadius) * 2;

    let arc = d3.arc().innerRadius(innerRadius).outerRadius(outerRadius);

    let pie = d3.pie().value(d => d.value);

    let _current = null;

    function arcTween(a) {
        let i = d3.interpolate(_current, a);
        _current = i(0);
        return (t) => arc(i(t));
    }

    function update(chart) {
        chart.selectAll("path").data(pie(data || [{ value: 1 }])).transition().duration(250)
            .attrTween("d", arcTween);
    }

    function draw(chart) {
        let g = chart.append('g')
            .attr('width', diameter)
            .attr('height', diameter)
            .attr('transform', `translate(${diameter / 2}, ${diameter / 2})`);

        if (statistic.header) {
            g.append('text')
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .attr('class', 'PieChart__statistic-header')
                .style('font-size', `${innerRadius * 0.4}px`).text(statistic.header);

            if (statistic.subtitle) {
                g.append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dominant-baseline', 'hanging')
                    .attr('class', 'PieChart__statistic-subtitle')
                    .style('font-size', `${innerRadius * 0.2}px`)
                    .attr('dy', '1em')
                    .text(statistic.subtitle)
            }
        }

        if (!sorted) {
            pie.sort(null);
        }

        let slices = pie(data || [{ value: 1 }]);
        let color = d3.scaleOrdinal(d3.schemeCategory10);

        let path = g.selectAll('path.slice')
            .data(slices).enter()
            .append('path')
            .attr('class', `PieChart__slice`)
            .attr('fill', d => {
                if (!data) return '#DBDBDB';
                if (!colorFunction) return color(d.value);
                return colorFunction(d.data.name);
            })
            .on("mouseover", function (event, d) {
                const slice = d3.select(this);
                const newFill = (!data && "#DBDBDB") ||
                    (hoverColors && hoverColors[d.data.name]) ||
                    (colorFunction ? colorFunction(d.data.name) : color(d.value));

                slice
                    .transition()
                    .duration(200)
                    .attr("fill", newFill)
                    .attr("d", d3.arc()
                        .innerRadius(innerRadius)
                        .outerRadius(outerRadius * (expanding ? 1.1 : 1))
                    );

                if (showTooltip && data) {
                    const centroid = arc.centroid(d);
                    const name = d?.data?.name || "";
                    const value = d?.data?.value ?? d.value;

                    tooltipContainer.style("display", null);
                    tooltipContainer.selectAll("*").remove();

                    const fo = tooltipContainer
                        .append("foreignObject")
                        .attr("width", 1)
                        .attr("height", 1)
                        .attr("x", 0)
                        .attr("y", 0)
                        .attr("pointer-events", "none");

                    const div = fo.append("xhtml:div")
                        .attr("class", "ui tiny segment compact PieChart__tooltip-wrapper")
                        .style("pointer-events", "none")
                        .style("font-size", "7px")
                        .style("padding", "4px")
                        .style("margin", "0")
                        .html(`
            <div class="ui statistic PieChart__tooltip">
                <div class="label PieChart__tooltip-label">${name}</div>
                <div class="value PieChart__tooltip-value">${value}</div>
            </div>
        `);

                    requestAnimationFrame(() => {
                        const node = div.node();
                        const bbox = node.getBoundingClientRect();
                        const w = bbox.width;
                        const h = bbox.height / 2;

                        fo.attr("width", w).attr("height", h);

                        const offset = 8;
                        const half = diameter / 2;
                        let xPos = centroid[0] - w / 2;
                        let yPos = centroid[1] - h - offset;
                        if (yPos < -half) {
                            yPos = centroid[1] + offset;
                        }
                        if (xPos < -half) {
                            xPos = -half + 4;
                        }
                        if (xPos + w > half) {
                            xPos = half - w - 4;
                        }
                        tooltipContainer.attr("transform", `translate(${xPos}, ${yPos})`);
                    });
                }



                onMouseOver(d);
            })
            .on("mouseout", function (event, d) {
                const slice = d3.select(this);

                if (showTooltip) {
                    tooltipContainer.style("display", "none");
                }

                slice
                    .transition()
                    .duration(200)
                    .attr("fill", () =>
                        (!data && "#DBDBDB") || (colorFunction ? colorFunction(d.data.name) : color(d.value))
                    )
                    .attr("d", arc);

                onMouseOut();
            });

        const tooltipContainer = g.append('g')
            .attr('class', 'tooltip-container')
            .style('pointer-events', 'none')
            .style('display', 'none');

        function animate(path) {
            let enterClockwise = {
                startAngle: 0,
                endAngle: 0
            };

            let enterAntiClockwise = {
                startAngle: Math.PI * 2,
                endAngle: Math.PI * 2
            };

            if (animation) {
                let entryAngles = animation.type === 'clockwise' ? enterClockwise : enterAntiClockwise;
                path.attr('d', arc(entryAngles))
                    .each(d => {
                        _current = {
                            data: d.data,
                            value: d.value,
                            startAngle: entryAngles.startAngle,
                            endAngle: entryAngles.endAngle
                        }
                    });
                path.transition()
                    .duration(animation.duration)
                    .attrTween('d', arcTween)
            } else {
                path.attr('d', arc);
            }
        }

        animate(path);
    }
    return (
        <ChartComponent
            draw={chart => draw(chart)}
            update={update}
            isUpdateDisabled={isUpdateDisabled}
        />
    );
}

export default Pie;
